import api from '.';
import { PaginationProps } from '../common/interface/pagination';
import {
  CivilTemplateDocument,
  DocumentWithProjectUser,
  ElectricalTemplateDocument
} from '../pages/MasterData/Document/documentSlice';

interface GetProjectUserParams {
  id: number;
  projectId: number;
}

interface GetDocumentBulkPlanningMasterDataParams {
  disciplineIds: number[];
  search: string | null;
}

interface GetDocumentMasterDataParams extends PaginationProps {}

export interface GetBulkPlanningDocumentParams {
  projectId: number;
  disciplineId: number;
  ids?: number[];
  plant?: string | null;
  area?: string | null;
  instrumentListIds?: number[];
}

export interface GetBulkPlanningWithQuantity
  extends Omit<GetBulkPlanningDocumentParams, 'ids' | 'instrumentListIds'> {
  documents: { documentId: number; quantity: number }[];
}

export interface GetPipingBulkPlanningDocumentParams {
  pipingWorkTypeId: number;
  pipingPlantTypeId: number | null;
  pipingModelTypeId: number | null;
  pipingListIds: number[];
}

export interface GetDocumentStandardWorkflowParams extends PaginationProps {
  disciplineIds?: number[];
  search?: string | null;
}

export interface GetDocumentsMasterDataPlugAndPlayParams extends PaginationProps {
  disciplineIds?: number[];
  aimsDocumentTypeIds?: number[];
  search?: string | null;
}

export interface GetCivilBulkPlanningDocument {
  civilJobLists: { civilJobListId: number; quantity: number }[];
}

export interface GetElectricalBulkPlanningDocumentParams {
  electricalListIds: number[];
}

export interface CreateDocument {
  name: string;
  no: string;
  disciplineId: number;
  aimsDocumentTypeId: number;
  documentGroupId: number | null;
}

export interface UpdateDocument extends CreateDocument {
  id: number | null;
  isActive: boolean;
}

interface UpdateActiveDocument {
  id: number | null;
  isActive: boolean;
}

interface ReturnTypeBulkPlanningDocument {
  data: DocumentWithProjectUser[];
}

interface ReturnTypeCivilBulkPlanningDocument {
  data: CivilTemplateDocument[];
}

interface ReturnTypeElectricalBulkPlanningDocument {
  data: ElectricalTemplateDocument[];
}

export async function getDocuments() {
  const { data } = await api.get('/documents');
  return data;
}

export async function getDocumentsBulkPlanningMasterData(
  params: GetDocumentBulkPlanningMasterDataParams
) {
  const { data } = await api.get('/documents', { params });
  return data;
}

export async function getDocumentsMasterData(
  params: GetDocumentMasterDataParams
) {
  const { data } = await api.get('/documents/master-data', { params });
  return data;
}

export async function getBulkPlanningDocuments(
  params: GetBulkPlanningDocumentParams
) {
  const { data } = await api.get('/documents/bulk-planning', { params });
  return data;
}

export async function getBulkPlanningWithQuantity(
  getBulkPlanningWithQuantity: GetBulkPlanningWithQuantity
): Promise<ReturnTypeBulkPlanningDocument> {
  const { data } = await api.post(
    '/documents/bulk-planning-with-quantity',
    getBulkPlanningWithQuantity
  );
  return data;
}

export async function getPipingBulkPlanningDocuments(
  params: GetPipingBulkPlanningDocumentParams
) {
  const { data } = await api.get('/documents/pip-bulk-planning', { params });
  return data;
}

export async function getPipingProjectDocuments(projectId: number) {
  const params = { projectId };
  const { data } = await api.get('/documents/pip-review-documents', { params });
  return data;
}

export async function getCivilBulkPlanningDocuments(
  getCivilBulkPlanningDocument: GetCivilBulkPlanningDocument
): Promise<ReturnTypeCivilBulkPlanningDocument> {
  const { data } = await api.post(
    '/documents/civil-bulk-planning',
    getCivilBulkPlanningDocument
  );
  return data;
}

export async function getElectricalBulkPlanningDocuments(
  params: GetElectricalBulkPlanningDocumentParams
): Promise<ReturnTypeElectricalBulkPlanningDocument> {
  const { data } = await api.get('/documents/electrical-bulk-planning', {
    params
  });
  return data;
}

export async function getDocumentStandardWorkflows(
  params: GetDocumentStandardWorkflowParams
) {
  const { data } = await api.get('/documents/standard-workflows', { params });
  return data;
}

export async function getDocumentsMasterDataPlugAndPlay(
  params: GetDocumentsMasterDataPlugAndPlayParams
) {
  const { data } = await api.get('/documents/master-data', { params });
  return data;
}

export async function getDocument(id: number) {
  const { data } = await api.get(`/documents/${id}`);
  return data;
}

export async function getDocumentWithProjectUsers(
  params: GetProjectUserParams
) {
  const { data } = await api.get(`/documents/${params.id}/project-users`, {
    params
  });
  return data;
}

export async function createDocument(createDocument: CreateDocument) {
  const { data } = await api.post('/documents', createDocument);
  return data;
}

export async function updateDocument(updateDocument: UpdateDocument) {
  const { data } = await api.put(
    `/documents/${updateDocument.id}`,
    updateDocument
  );
  return data;
}

export async function updateActiveDocument(
  updateActiveDocument: UpdateActiveDocument
) {
  const { data } = await api.put(
    `/documents/${updateActiveDocument.id}/active`,
    updateActiveDocument
  );
  return data;
}

export function deleteDocument(id: number) {
  return api.delete(`/documents/${id}`);
}
